import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
	ArrowLeft,
	Bookmark,
	BookmarkX,
	Briefcase,
	Building2,
	Link2,
	MessageSquareWarning,
	Search,
	Trash2,
	User,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { SaveMessageDialog } from "@/components/common/SaveMessageDialog";
import { EmailContentFrame } from "@/components/common/EmailContentFrame";
import { useSettings } from "@/providers/SettingsProvider";
import {
	useDeleteSavedMessage,
	useSavedMessages,
	type SavedMessage,
} from "@/hooks/inbox/useSavedMessages";
import { useQueryClient } from "@tanstack/react-query";
import { inboxKeys } from "@/hooks/inbox/queryKeys";
import type { EntityMailboxType } from "@/hooks/inbox/useEntityConversations";
import { ChannelBadge } from "./ChannelBadge";
import type { ChannelFilterValue } from "./InboxProvider";
import { ChannelFilter } from "./ChannelFilter";
import {
	formatFullDateTime,
	formatRelativeTime,
	getChannel,
	isHTMLContent,
	toSnippet,
} from "./utils";

interface SavedParticipant {
	id?: string;
	name?: string;
	display_name?: string;
	email?: string;
	role?: string;
	picture_url?: string;
	participant_type?: string;
	specifics?: { provider?: string };
}

const ParticipantChips: React.FC<{ participants: SavedParticipant[] }> = ({ participants }) => {
	const { t } = useTranslation();
	return (
		<div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
			<User className="h-3 w-3 shrink-0" />
			<span className="font-medium">{t("inbox.saved.participants", "Participants")}:</span>
			{participants.map((participant, index) => {
				const name =
					participant.display_name || participant.name || participant.email || t("inbox.unknownSender", "Unknown");
				const isLinkedIn =
					participant.participant_type === "LINKEDIN" || participant.specifics?.provider === "LINKEDIN";
				return (
					<span key={participant.id || index} className="inline-flex items-center gap-1">
						<span className="font-medium text-foreground">{name}</span>
						{participant.role && (
							<Badge variant="outline" className="px-1.5 py-0 text-[10px] font-normal">
								{participant.role}
							</Badge>
						)}
						{isLinkedIn && (
							<Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-normal">
								LinkedIn
							</Badge>
						)}
					</span>
				);
			})}
		</div>
	);
};

const LinkedEntityPills: React.FC<{ message: SavedMessage }> = ({ message }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const pills = [
		...message.linkedCandidates.map((candidate) => ({
			key: `candidate-${candidate.id}`,
			label: candidate.name,
			icon: User,
			path: `/candidates/${candidate.id}`,
		})),
		...message.linkedJobs.map((job) => ({
			key: `job-${job.id}`,
			label: job.title,
			icon: Briefcase,
			path: `/jobs/${job.id}`,
		})),
		...message.linkedCompanies.map((company) => ({
			key: `company-${company.id}`,
			label: company.name,
			icon: Building2,
			path: `/companies/${company.id}`,
		})),
	];
	if (pills.length === 0) return null;

	return (
		<div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
			<Link2 className="h-3 w-3 shrink-0" />
			<span className="font-medium">{t("inbox.saved.linkedTo", "Linked to")}:</span>
			{pills.map((pill) => (
				<Button
					key={pill.key}
					variant="outline"
					size="sm"
					className="h-6 gap-1 rounded-sm px-2 text-xs font-normal"
					onClick={() => navigate(pill.path)}
				>
					<pill.icon className="h-3 w-3" />
					{pill.label}
				</Button>
			))}
		</div>
	);
};

interface SavedMessagesProps {
	entityType: EntityMailboxType;
	entityId: string;
}

/**
 * Saved messages linked to the host entity: scannable list + detail with
 * sanitized content, relation editing and deletion. Replaces the old
 * SavedMessagesTab.
 */
export const SavedMessages: React.FC<SavedMessagesProps> = ({ entityType, entityId }) => {
	const { t, i18n } = useTranslation();
	const queryClient = useQueryClient();
	const settings = useSettings();
	const isDarkMode =
		settings.colorMode === "dark" ||
		(settings.colorMode === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);

	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [channelFilter, setChannelFilter] = useState<ChannelFilterValue>("all");
	const [editingMessage, setEditingMessage] = useState<SavedMessage | null>(null);

	const { data, isLoading, isError, isSupported, refetch } = useSavedMessages(entityType, entityId);
	const deleteMutation = useDeleteSavedMessage();

	const filtered = useMemo(() => {
		let messages = data || [];
		if (channelFilter !== "all") {
			messages = messages.filter(
				(message) => getChannel({ conversation_type: message.message_type }) === channelFilter
			);
		}
		const query = search.trim().toLowerCase();
		if (query) {
			messages = messages.filter((message) =>
				[
					message.subject,
					message.message,
					message.added_by_profile?.first_name,
					message.added_by_profile?.last_name,
					message.added_by_profile?.email,
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase()
					.includes(query)
			);
		}
		return messages;
	}, [data, channelFilter, search]);

	const selectedMessage = filtered.find((message) => message.id === selectedId);

	const handleDelete = (messageId: string) => {
		deleteMutation.mutate(messageId, {
			onSuccess: () => {
				if (selectedId === messageId) setSelectedId(null);
			},
		});
	};

	if (!isSupported) {
		return (
			<EmptyState
				icon={BookmarkX}
				title={t("inbox.saved.matchUnavailableTitle", "Not available for matches")}
				description={t(
					"inbox.saved.matchUnavailableDescription",
					"Saved messages can't be linked to matches yet."
				)}
				className="h-full"
			/>
		);
	}

	if (selectedMessage) {
		const savedBy = [
			selectedMessage.added_by_profile?.first_name,
			selectedMessage.added_by_profile?.last_name,
		]
			.filter(Boolean)
			.join(" ");
		const participants = Array.isArray(selectedMessage.participants)
			? (selectedMessage.participants as SavedParticipant[])
			: [];

		return (
			<div className="flex h-full min-h-0 flex-col">
				<header className="flex shrink-0 items-start gap-3 border-b border-border p-4">
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8 shrink-0 -ml-1"
						onClick={() => setSelectedId(null)}
						aria-label={t("inbox.back", "Back")}
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div className="min-w-0 flex-1">
						<div className="flex flex-wrap items-center gap-2">
							<h3 className="truncate text-sm font-semibold text-foreground">
								{selectedMessage.subject || t("inbox.saved.untitled", "Saved message")}
							</h3>
							<ChannelBadge channel={getChannel({ conversation_type: selectedMessage.message_type })} />
						</div>
						<p className="mt-0.5 text-xs text-muted-foreground">
							{savedBy && t("inbox.saved.savedBy", "Saved by {{name}}", { name: savedBy })}
							{savedBy && " · "}
							{formatFullDateTime(selectedMessage.created_at, i18n.language)}
						</p>
					</div>
					<div className="flex shrink-0 gap-1.5">
						<Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setEditingMessage(selectedMessage)}>
							<Link2 className="h-3.5 w-3.5" />
							{t("inbox.saved.editRelations", "Edit relations")}
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="h-8 gap-1.5 text-destructive hover:text-destructive"
							onClick={() => handleDelete(selectedMessage.id)}
							disabled={deleteMutation.isPending}
						>
							<Trash2 className="h-3.5 w-3.5" />
							{t("inbox.saved.delete", "Delete")}
						</Button>
					</div>
				</header>

				<div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin bg-muted/30 p-4">
					<div className="mx-auto max-w-3xl space-y-3">
						{participants.length > 0 && <ParticipantChips participants={participants} />}
						<LinkedEntityPills message={selectedMessage} />
						{(participants.length > 0 ||
							selectedMessage.linkedCandidates.length +
								selectedMessage.linkedJobs.length +
								selectedMessage.linkedCompanies.length >
								0) && <Separator />}
						<div className="rounded-sm border border-border bg-card p-4 shadow-card">
							{isHTMLContent(selectedMessage.message) ? (
								<EmailContentFrame content={selectedMessage.message} isDarkMode={isDarkMode} />
							) : (
								<div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
									{selectedMessage.message}
								</div>
							)}
						</div>
					</div>
				</div>

				{editingMessage && (
					<SaveMessageDialog
						isOpen={!!editingMessage}
						onClose={() => setEditingMessage(null)}
						messageData={{
							id: editingMessage.original_message_key || editingMessage.id,
							text: editingMessage.message,
							subject: editingMessage.subject,
							messageType: editingMessage.message_type,
							participants: editingMessage.participants,
						}}
						editMode={true}
						existingSavedMessageId={editingMessage.id}
						existingRelations={{
							candidates: editingMessage.linkedCandidates,
							jobs: editingMessage.linkedJobs,
							companies: editingMessage.linkedCompanies,
						}}
						onMessageSaved={() => {
							queryClient.invalidateQueries({ queryKey: [...inboxKeys.all, "saved"] });
							setEditingMessage(null);
						}}
					/>
				)}
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="shrink-0 space-y-2 border-b border-border p-3">
				<div className="relative">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder={t("inbox.saved.searchPlaceholder", "Search saved messages…")}
						className="h-9 pl-8"
						aria-label={t("inbox.saved.searchPlaceholder", "Search saved messages…")}
					/>
				</div>
				<ChannelFilter value={channelFilter} onChange={setChannelFilter} />
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
				{isLoading ? (
					<div className="space-y-1 py-2" aria-hidden>
						{Array.from({ length: 3 }).map((_, index) => (
							<div key={index} className="space-y-2 px-3 py-2">
								<div className="flex justify-between gap-4">
									<Skeleton className="h-3.5 w-1/3" />
									<Skeleton className="h-3 w-10" />
								</div>
								<Skeleton className="h-3 w-3/4" />
							</div>
						))}
					</div>
				) : isError ? (
					<div className="flex h-full flex-col items-center justify-center p-6">
						<EmptyState
							icon={MessageSquareWarning}
							title={t("inbox.saved.errorTitle", "Couldn't load saved messages")}
							description={t(
								"inbox.saved.errorDescription",
								"Something went wrong while loading saved messages. Try again."
							)}
						/>
						<Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
							{t("inbox.entity.retry", "Retry")}
						</Button>
					</div>
				) : filtered.length === 0 ? (
					<EmptyState
						icon={Bookmark}
						title={t("inbox.saved.emptyTitle", "No saved messages")}
						description={t("inbox.saved.emptyDescription", "Messages you save will appear here.")}
						className="h-full"
					/>
				) : (
					<ul className="divide-y divide-border/60">
						{filtered.map((message) => {
							const linkedCount =
								message.linkedCandidates.length + message.linkedJobs.length + message.linkedCompanies.length;
							const savedBy = [
								message.added_by_profile?.first_name,
								message.added_by_profile?.last_name,
							]
								.filter(Boolean)
								.join(" ");
							return (
								<li key={message.id} className="group relative">
									<button
										type="button"
										onClick={() => setSelectedId(message.id)}
										className="flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
									>
										<div className="flex w-full items-baseline justify-between gap-2">
											<span className="flex min-w-0 items-center gap-2">
												<span className="truncate text-sm font-medium text-foreground">
													{message.subject || t("inbox.saved.untitled", "Saved message")}
												</span>
												<ChannelBadge
													channel={getChannel({ conversation_type: message.message_type })}
													className="shrink-0"
												/>
											</span>
											<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
												{formatRelativeTime(message.created_at, i18n.language, t("inbox.yesterday", "Yesterday"))}
											</span>
										</div>
										<span className="truncate pr-8 text-xs text-muted-foreground">
											{toSnippet(message.message, 120)}
										</span>
										<span className="text-xs text-muted-foreground/80">
											{savedBy && t("inbox.saved.savedBy", "Saved by {{name}}", { name: savedBy })}
											{savedBy && linkedCount > 0 && " · "}
											{linkedCount > 0 &&
												t("inbox.saved.linkedCount", "Linked to {{count}} items", { count: linkedCount })}
										</span>
									</button>
									<Button
										variant="ghost"
										size="icon"
										className="absolute right-2 top-2 h-7 w-7 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
										onClick={() => handleDelete(message.id)}
										disabled={deleteMutation.isPending}
										aria-label={t("inbox.saved.delete", "Delete")}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</div>
	);
};
